import { createDirectChoices } from '../ui/15-direct-choices.mjs';
// Owns collections rendering and interactions.
// Dependencies are injected by app.mjs; feature modules never import one another.
/** Create saved-post and collection views from the current server snapshot. */
export function createCollections(ctx) {
  function renderSaved() {
    ctx.view.append(
      ctx.heading('Worth coming back to.', 'Private collections for the moments you want to keep.'),
    );
    const controls = ctx.e('div', 'collection-controls');
    if (!ctx.state.settings.collections.some((c) => c.id === ctx.savedCollection))
      ctx.savedCollection = 'all';
    const choices = createDirectChoices(ctx, {
      label: ctx.uiText('Saved collection'),
      name: 'saved-collection',
      variant: 'filter',
      searchable: true,
      value: ctx.savedCollection,
      options: [
        { value: 'all', label: 'All saved moments', icon: 'bookmark' },
        ...ctx.state.settings.collections.map((c) => ({
          value: c.id,
          label: c.name,
          literal: true,
          icon: 'bookmark',
        })),
      ],
      onSelect: (value) => {
        ctx.savedCollection = value;
        ctx.render();
      },
    });
    controls.append(
      choices.node,
      ctx.button('New collection', () => newCollection()),
    );
    const collection = ctx.state.settings.collections.find((c) => c.id === ctx.savedCollection);
    if (collection)
      controls.append(
        ctx.button(
          'Delete collection',
          () =>
            ctx.dialog(
              'Delete collection?',
              ctx.append(
                ctx.e('div', 'form-stack'),
                ctx.e('p', '', 'Your saved posts remain in All saved moments.'),
                ctx.button(
                  'Delete collection',
                  async () => {
                    await ctx.command({
                      type: 'collection-delete',
                      id: collection.id,
                    });
                    ctx.savedCollection = 'all';
                    ctx.modal.close();
                    ctx.render();
                  },
                  'primary-button',
                ),
              ),
            ),
          'text-button',
        ),
      );
    ctx.view.append(controls);
    const posts = ctx.state.posts.filter(
      (p) =>
        ctx.state.settings.saved.includes(p.id) &&
        (!collection || collection.postIds.includes(p.id)),
    );
    if (!posts.length)
      ctx.view.append(
        ctx.empty('No available moments here. Save a post, then use Options to organize it.'),
      );
    for (const p of posts) {
      if (collection)
        ctx.view.append(
          ctx.button(
            'Remove from this collection',
            () =>
              ctx.command({
                type: 'collection-post',
                id: p.id,
                collectionId: collection.id,
                saved: false,
              }),
            'text-button',
          ),
        );
      ctx.view.append(ctx.postCard(p));
    }
  }

  function newCollection(after) {
    const form = ctx.e('form', 'form-stack');
    const name = ctx.field('input', {
      required: true,
      maxLength: 60,
      placeholder: 'Travel ideas, things to try…',
      ariaLabel: 'Collection name',
    });
    const save = ctx.e('button', 'primary-button', 'Create collection');
    const error = ctx.e('p', 'form-error');
    error.setAttribute('role', 'alert');
    form.append(ctx.e('p', 'subtle', 'Only you can see your collections.'), name, error, save);
    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      save.disabled = true;
      try {
        await ctx.command({ type: 'collection-create', name: name.value }, { paint: false });
        if (after) after();
        else {
          ctx.modal.close();
          ctx.render();
        }
      } catch (err) {
        error.textContent = err.message;
      } finally {
        save.disabled = false;
      }
    });
    ctx.dialog('New private collection', form);
  }

  function collectionDialog(post) {
    const body = ctx.e('div', 'form-stack');
    body.append(
      ctx.e(
        'p',
        'subtle',
        'Only you can see these collections. A post remains subject to its author’s audience and expiry.',
      ),
    );
    for (const c of ctx.state.settings.collections) {
      const included = c.postIds.includes(post.id);
      const b = ctx.button((included ? 'Remove from ' : 'Add to ') + c.name, async () => {
        await ctx.command(
          {
            type: 'collection-post',
            id: post.id,
            collectionId: c.id,
            saved: !included,
          },
          { paint: false },
        );
        ctx.render();
        collectionDialog(post);
      });
      b.setAttribute('aria-pressed', String(included));
      body.append(b);
    }
    body.append(ctx.button('New collection', () => newCollection(() => collectionDialog(post))));
    ctx.dialog('Organize this moment', body);
  }
  return { renderSaved, newCollection, collectionDialog };
}
